import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Compass, Loader2, ArrowLeft, CheckCircle2, Mail, Lock, Inbox } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const [step, setStep] = useState(tokenFromUrl ? 'new-password' : 'request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/request-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      setStep('email-sent');
      toast.success(data.message);
    } catch {
      toast.error('Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenFromUrl, new_password: newPassword }),
      });
      if (!response.ok) {
        let msg = 'Error al restablecer contraseña';
        if (!response.bodyUsed) {
          try { const d = await response.json(); msg = d.detail || msg; } catch {}
        }
        throw new Error(msg);
      }
      toast.success('¡Contraseña actualizada! Ya puedes iniciar sesión.');
      navigate('/login');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Compass className="w-8 h-8 text-[#2563EB]" />
          <span className="font-bold text-2xl tracking-tight font-['Manrope']">Klarvi</span>
        </div>
        <Card className="border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-['Manrope']">
              {step === 'request' && 'Recuperar contraseña'}
              {step === 'email-sent' && 'Revisa tu correo'}
              {step === 'new-password' && 'Nueva contraseña'}
            </CardTitle>
            <CardDescription>
              {step === 'request' && 'Ingresa tu correo para restablecer tu contraseña'}
              {step === 'email-sent' && 'Te enviamos un enlace para restablecer tu contraseña'}
              {step === 'new-password' && 'Elige tu nueva contraseña'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'request' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="pl-10"
                      required
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                  disabled={loading || !email}
                  data-testid="reset-request-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enviar enlace de recuperación
                </Button>
              </form>
            )}

            {step === 'email-sent' && (
              <div className="text-center space-y-4" data-testid="reset-email-sent">
                <div className="w-16 h-16 rounded-full bg-[#2563EB]/10 flex items-center justify-center mx-auto">
                  <Inbox className="w-8 h-8 text-[#2563EB]" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Si <span className="font-medium text-[#0F172A]">{email}</span> está registrado, recibirás un correo con un enlace para restablecer tu contraseña.
                </p>
                <p className="text-xs text-muted-foreground">
                  Revisa tu bandeja de entrada y spam. El enlace expira en 1 hora.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => { setStep('request'); setEmail(''); }}
                  data-testid="reset-try-again-btn"
                >
                  Intentar con otro correo
                </Button>
              </div>
            )}

            {step === 'new-password' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nueva contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="pl-10"
                      required
                      data-testid="reset-new-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu contraseña"
                      className="pl-10"
                      required
                      data-testid="reset-confirm-password"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                  disabled={loading || !newPassword || !confirmPassword}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Restablecer contraseña
                </Button>
              </form>
            )}

            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-[#2563EB] hover:underline flex items-center justify-center gap-1 mx-auto"
                data-testid="back-to-login"
              >
                <ArrowLeft className="w-3 h-3" />
                Volver al inicio de sesión
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
